// Wax blobs in a lava lamp. Each blob carries heat: warm wax is buoyant and
// rises, cools near the top and sinks again; nearby blobs pull together a
// little so they merge and stretch apart. Rendering is a metaball field
// (Blinn 1982; Wyvill et al. 1986) polygonized each frame with marching cubes
// (Lorensen and Cline 1987) through three.js' MarchingCubes helper. The heat,
// buoyancy and cohesion values are art direction, not measured wax.
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));

export class LavaBlobs{
  // profile(y) → inner radius of the bottle at height y (toy-local); bottom/top bound the liquid.
  constructor({center,bottom,top,profile,count=7,random=Math.random,radius=.16}={}){
    Object.assign(this,{center:[...center],bottom,top,profile,count,random});
    for(const key of ['x','y','z','vx','vy','vz','heat','r'])this[key]=new Float64Array(count);
    this.radius=radius;this.activity=0;this.time=0;
    for(let i=0;i<count;i++)this.r[i]=radius*(.75+.5*random());
    this.pool();
  }
  // All wax pooled at the bottom: the resting state.
  pool(){
    const span=this.profile(this.bottom+this.radius)*.55;
    for(let i=0;i<this.count;i++){
      const a=i/this.count*Math.PI*2+this.random()*.4,d=span*(.3+.6*this.random());
      this.x[i]=this.center[0]+Math.cos(a)*d;this.z[i]=this.center[2]+Math.sin(a)*d;
      this.y[i]=this.bottom+this.r[i]*.7+this.random()*this.r[i]*.4;
      this.vx[i]=this.vy[i]=this.vz[i]=0;this.heat[i]=0;
    }
    this.activity=0;
  }
  reset(){this.pool();}
  // Warm the wax near a point (a squeeze) and push it away from the finger.
  warm(point,amount=1,push=[0,0,0],reach=this.radius*3.2){
    if(!point?.every(Number.isFinite)||!Number.isFinite(amount))return;
    for(let i=0;i<this.count;i++){
      const d=Math.hypot(this.x[i]-point[0],this.y[i]-point[1],this.z[i]-point[2])/reach;if(d>=1)continue;
      const w=(1-d*d)**2;this.heat[i]=clamp(this.heat[i]+amount*w,0,1.4);
      this.vx[i]+=push[0]*w;this.vy[i]+=push[1]*w;this.vz[i]+=push[2]*w;
    }
    this.activity=1;
  }
  // The heater: every blob near the bottom warms up and starts to rise.
  heatBase(amount=1){
    // Small blobs warm faster and leave first, so the wax rises in turns, not as one lump.
    for(let i=0;i<this.count;i++){
      const low=1-clamp((this.y[i]-this.bottom)/(this.top-this.bottom),0,1),small=(this.radius/this.r[i])**1.6;
      this.heat[i]=clamp(this.heat[i]+amount*(.15+.95*low**2)*small*(.45+1.1*this.random()),0,1.4);
    }
    this.activity=1;
  }
  stir(point,velocity,reach=this.radius*3.5){
    if(!point?.every(Number.isFinite)||!velocity?.every(Number.isFinite))return;
    for(let i=0;i<this.count;i++){
      const d=Math.hypot(this.x[i]-point[0],this.y[i]-point[1],this.z[i]-point[2])/reach;if(d>=1)continue;
      const w=(1-d)*.6;this.vx[i]+=clamp(velocity[0],-4,4)*w;this.vy[i]+=clamp(velocity[1],-4,4)*w;this.vz[i]+=clamp(velocity[2],-4,4)*w;
    }
    this.activity=1;
  }
  step(dt){
    if(!Number.isFinite(dt)||dt<=0||this.activity===0)return;
    dt=Math.min(dt,1/30);this.time+=dt;
    const H=this.top-this.bottom,steps=Math.max(1,Math.ceil(dt/(1/120))),h=dt/steps;let motion=0,warmth=0;
    for(let s=0;s<steps;s++){
      for(let i=0;i<this.count;i++){
        const height=clamp((this.y[i]-this.bottom)/H,0,1);
        // Cooling is faster near the top; warm wax is buoyant, cold wax sinks.
        this.heat[i]=Math.max(0,this.heat[i]-h*(.12+.38*height**2));
        const buoyancy=(this.heat[i]-.32)*H*1.25;
        this.vy[i]+=buoyancy*h;
        // Cohesion: nearby wax pulls together, overlapping wax pushes apart.
        for(let j=i+1;j<this.count;j++){
          const dx=this.x[j]-this.x[i],dy=this.y[j]-this.y[i],dz=this.z[j]-this.z[i],d=Math.hypot(dx,dy,dz)||1e-6,rest=(this.r[i]+this.r[j])*.82;
          if(d>rest*1.6)continue;
          // Overlap pushes apart; a short-range pull lets touching wax merge and stretch.
          const f=(d<rest?(d-rest)*7:(d-rest)*.9*(1-(d-rest)/(rest*.6)))*h/d;
          this.vx[i]+=dx*f;this.vy[i]+=dy*f;this.vz[i]+=dz*f;this.vx[j]-=dx*f;this.vy[j]-=dy*f;this.vz[j]-=dz*f;
        }
        const drag=Math.exp(-2.4*h);this.vx[i]*=drag;this.vy[i]*=drag;this.vz[i]*=drag;
        this.x[i]+=this.vx[i]*h;this.y[i]+=this.vy[i]*h;this.z[i]+=this.vz[i]*h;
        // Stay inside the bottle.
        const r=this.r[i]*.75,low=this.bottom+r,high=this.top-r;
        // Wax resting on the bottom or the glass drags along it, so pooled wax settles.
        if(this.y[i]<low){this.y[i]=low;if(this.vy[i]<0)this.vy[i]*=-.15;const f=Math.exp(-8*h);this.vx[i]*=f;this.vz[i]*=f;}
        if(this.y[i]>high){this.y[i]=high;if(this.vy[i]>0)this.vy[i]*=-.15;}
        // Sideways, the wall is measured where the bottle is narrowest along the
        // blob's height, so wax near the tapered top does not poke through.
        const reach=this.r[i],wall=Math.min(this.profile(this.y[i]),this.profile(Math.min(this.top,this.y[i]+reach*.6)));
        const limit=Math.max(.01,wall-r),dx=this.x[i]-this.center[0],dz=this.z[i]-this.center[2],d=Math.hypot(dx,dz);
        if(d>limit){this.x[i]=this.center[0]+dx*limit/d;this.z[i]=this.center[2]+dz*limit/d;const vn=(this.vx[i]*dx+this.vz[i]*dz)/d;if(vn>0){this.vx[i]-=vn*dx/d;this.vz[i]-=vn*dz/d;}}
      }
    }
    for(let i=0;i<this.count;i++){motion=Math.max(motion,Math.abs(this.vx[i])+Math.abs(this.vy[i])+Math.abs(this.vz[i]));warmth=Math.max(warmth,this.heat[i]);}
    // Cold, still wax rests at the bottom and the lamp can idle.
    this.activity=motion<.004&&warmth<.02?0:motion+warmth;
    if(this.activity===0)for(let i=0;i<this.count;i++){this.vx[i]=this.vy[i]=this.vz[i]=0;this.heat[i]=0;}
  }
}
